import React, {useEffect, useRef, useState} from 'react';
import OdiaFsm from 'odia-keyboard/src/fsm';

export default function OdiaInput({
  type = 'text',
  onChange = (_v: string) => {},
  onClick = (_e: React.MouseEvent<HTMLInputElement>) => {},
  onFocus = (_e: React.FocusEvent<HTMLInputElement>) => {},
  value,
  ...rest
}: {
  type?: string;
  onChange?: (v: string) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string;
  [key: string]: unknown;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fsm] = useState(() => new OdiaFsm());
  const [pos, setPos] = useState<number | null>(null);
  const [hasHidden, setHasHidden] = useState(false);

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value || '';
  }, [value]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const target = e.currentTarget;
    const val = target.value;
    if (val === '' && !hasHidden) {
      fsm.reset();
    }
    let newHasHidden = false;

    if (e.ctrlKey && e.keyCode === 32) {
      e.preventDefault();
      fsm.toggleLang();
      setHasHidden(newHasHidden);
      return;
    }

    if (e.altKey || e.ctrlKey || e.metaKey) {
      setHasHidden(newHasHidden);
      setPos(target.selectionStart);
      return;
    }
    const char = e.key;
    if (char.length !== 1) {
      if (target.selectionStart !== pos || char.startsWith('Arrow')) {
        fsm.reset();
      }
      setPos(target.selectionStart);
      setHasHidden(newHasHidden);
      return;
    }

    e.preventDefault();
    const [dist, rawStr] = fsm.input(char);
    let str = rawStr;
    if (str === null) {
      newHasHidden = true;
      str = '';
    }
    const start = target.selectionStart ?? 0;
    const left = val.substring(0, start + dist);
    const right = val.substring(start, val.length);
    const end =
      str !== undefined && str !== null
        ? start + dist + str.length
        : start + dist;

    target.value = left + str + right;
    target.setSelectionRange(end, end);
    if (onChange) onChange(target.value);
    setPos(target.selectionStart);
    setHasHidden(newHasHidden);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (onChange) onChange(e.currentTarget.value);
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    if (e.currentTarget.selectionStart !== pos) {
      fsm.reset();
    }
    setPos(e.currentTarget.selectionStart);
    if (onFocus) onFocus(e);
  }

  function handleClick(e: React.MouseEvent<HTMLInputElement>) {
    if (e.currentTarget.selectionStart !== pos) {
      fsm.reset();
    }
    setPos(e.currentTarget.selectionStart);
    if (onClick) onClick(e);
  }

  return (
    <input
      type={type}
      ref={inputRef}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onFocus={handleFocus}
      onClick={handleClick}
      {...rest}
    />
  );
}
